import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Box, Typography, Button, IconButton, CircularProgress, Snackbar, Alert } from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import AudienceBuilder from './AudienceBuilder';

const AudienceModal = ({ open, onClose, audience, onDelete, onUpdate, onSave, mode }) => {
  const [currentAudience, setCurrentAudience] = useState(audience);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  useEffect(() => {
    // Only update currentAudience if the incoming audience is different
    if (audience) {
      if (audience.id !== currentAudience?.id) {
        setCurrentAudience(audience);
      }
    }
  }, [audience, currentAudience?.id]);

  const handleAudienceChange = useCallback((updatedAudienceData) => {
    // Always store the most recent version of the audience data
    setCurrentAudience(prevAudience => {
      // Ensure we preserve the ID and other metadata while updating the groups/conditions
      return {
        ...prevAudience,
        name: updatedAudienceData.name || prevAudience.name,
        groups: updatedAudienceData.groups || prevAudience.groups
      };
    });
  }, []);

  const handleSaveChanges = async () => {
    if (!currentAudience) return;
    
    setIsUpdating(true);
    setError(null);

    if (!currentAudience?.name || currentAudience.name.trim() === '') {
      setError('Audience name is required.');
      setIsUpdating(false);
      return;
    }

    const allGroup = currentAudience.groups?.find(g => g.type === 'ALL');
    const hasValidAllCondition = allGroup?.conditions?.some(
      c => c.recordType && (c.fieldName || c.field) && c.operator && c.value !== undefined && c.value !== ''
    );

    if (!hasValidAllCondition) {
      setError('At least one valid ALL filter condition must be completed.');
      setIsUpdating(false);
      return;
    }
    
    try {
      // Prepare the payload in the format expected by the API
      const payload = {
        audience_id: currentAudience.audience_id || currentAudience.id,
        filter_name: currentAudience.name || currentAudience.filter_name,
        visibility: currentAudience.visibility || 'private',
        match_all: [],
        match_any: []
      };
      
      // Process ALL conditions
      const allGroup = currentAudience.groups?.find(g => g.type === 'ALL');
      
      if (allGroup && allGroup.conditions && allGroup.conditions.length > 0) {
        const validConditions = allGroup.conditions.filter(
          c => c.recordType && (c.fieldName || c.field) && c.operator
        );
        
        if (validConditions.length > 0) {
          payload.match_all = [{
            conjunction: "AND",
            conditions: validConditions.map(c => {
              // Create proper field reference
              const field = c.fieldName 
                ? `${c.recordType}.${c.fieldName}` 
                : (c.field ? `${c.recordType}.${c.field}` : undefined);
              
              return {
                field: field,
                operator: c.operator,
                value: c.value
              };
            }).filter(c => c.field) // Remove any invalid conditions
          }];
        }
      }
      
      // Process ANY conditions
      const anyGroup = currentAudience.groups?.find(g => g.type === 'ANY');
      
      if (anyGroup && anyGroup.conditions && anyGroup.conditions.length > 0) {
        const validConditions = anyGroup.conditions.filter(
          c => c.recordType && (c.fieldName || c.field) && c.operator
        );
        
        if (validConditions.length > 0) {
          payload.match_any = [{
            conjunction: "OR",
            conditions: validConditions.map(c => {
              // Create proper field reference
              const field = c.fieldName 
                ? `${c.recordType}.${c.fieldName}` 
                : (c.field ? `${c.recordType}.${c.field}` : undefined);
              
              return {
                field: field,
                operator: c.operator,
                value: c.value
              };
            }).filter(c => c.field) // Remove any invalid conditions
          }];
        }
      }
      
      // Call onSave or onUpdate depending on mode
      if (mode === 'create') {
        if (onSave) {
          await onSave(payload);
        }
      } else {
        if (onUpdate) {
          await onUpdate(payload);
        }
      }
      // Show success snackbar instead of alert
      setSuccessMessage('Audience saved successfully!');
      setShowSuccessSnackbar(true);
      // Close the modal after successful update
      onClose();
    } catch (err) {
      console.error('Failed to update audience:', err);
      setError('Failed to update audience: ' + (err.message || 'Unknown error'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!currentAudience) return;

    const confirmDelete = window.confirm('Are you sure you want to delete this audience?');
    if (!confirmDelete) return;

    if (onDelete && (currentAudience.id || currentAudience.audience_id)) {
      const idToDelete = currentAudience.audience_id || currentAudience.id;
      
      try {
        await onDelete(idToDelete);
        setSuccessMessage('Audience deleted successfully!');
        setShowSuccessSnackbar(true);
        onClose();
      } catch (err) {
        console.error('Failed to delete audience:', err);
        setError('Failed to delete audience: ' + (err.message || 'Unknown error'));
      }
    }
  };
  
  if (typeof open !== 'boolean' || !open) {
    return null;
  }

  // Get audience name from the right property
  const audienceName = currentAudience?.name || currentAudience?.filter_name || "Audience";

  return (
    <Modal open={open} onClose={onClose}>
      <>
        <Box
          sx={{
            width: { xs: '95%', sm: '85%', md: '75%' },
            margin: 'auto',
            marginTop: '5%',
            padding: 3,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: 24,
            position: 'relative',
            maxHeight: '85vh',
            overflowY: 'auto',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" fontWeight="medium">
              {mode === 'create' ? 'Create New Audience' : audienceName}
            </Typography>
            
            <Box>
              {(currentAudience?.visibility === 'private' || !currentAudience?.visibility) && (
                <IconButton
                  color="error"
                  onClick={handleDelete}
                  sx={{ mr: 1 }}
                  aria-label="Delete audience"
                  disabled={isUpdating}
                >
                  <DeleteIcon />
                </IconButton>
              )}
              
              <IconButton
                onClick={onClose}
                sx={{ color: 'gray' }}
                aria-label="Close"
                disabled={isUpdating}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {error && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1, color: '#c62828' }}>
              {error}
            </Box>
          )}

          <AudienceBuilder
            data={currentAudience}
            onUpdate={handleAudienceChange}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button 
              variant="outlined" 
              onClick={onClose} 
              sx={{ mr: 2 }}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSaveChanges}
              disabled={isUpdating}
              startIcon={isUpdating ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isUpdating ? 'Saving...' : (mode === 'create' ? 'Create Audience' : 'Save Changes')}
            </Button>
          </Box>
        </Box>
        <Snackbar
          open={showSuccessSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowSuccessSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setShowSuccessSnackbar(false)} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
      </>
    </Modal>
  );
};

export default AudienceModal;